import { TrackableFulfillmentType } from "@redotech/redo-model/conversation/message-parsers/track-order-message-parser";
import classNames from "classnames";
import { memo } from "react";
import {
  RedoBadge,
  RedoBadgeColor,
} from "../../arbiter-components/badge/redo-badge";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { ExternalLink } from "../../link";
import { ProductInfoCardButton } from "../../product-info-card-button";
import { Text } from "../../text";
import { Carousel } from "../product-display-carousel";
import * as orderTrackingInfoMessageCss from "./order-tracking-info-message.module.css";

export const OrderTrackingInfoMessage = memo(function OrderTrackingInfoMessage({
  trackableFulfillmentsToDisplay,
  storeUrl,
  messageSide = "left",
}: {
  trackableFulfillmentsToDisplay: TrackableFulfillmentType;
  storeUrl: string | undefined;
  messageSide?: "left" | "right";
}) {
  if (trackableFulfillmentsToDisplay.length === 0) {
    return null;
  }

  if (trackableFulfillmentsToDisplay.length === 1) {
    return (
      <FulfillmentCard
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fulfillment={trackableFulfillmentsToDisplay[0]}
        messageSide={messageSide}
        storeUrl={storeUrl}
      />
    );
  }

  return (
    <div className={orderTrackingInfoMessageCss.orderTrackingCarouselWrapper}>
      <Carousel perView={1.2} spacing={2}>
        {trackableFulfillmentsToDisplay.map((fulfillment, index) => (
          <FulfillmentCard
            fulfillment={fulfillment}
            key={index}
            messageSide={messageSide}
            storeUrl={storeUrl}
          />
        ))}
      </Carousel>
    </div>
  );
});

const FulfillmentCard = memo(function FulfillmentCard({
  fulfillment,
  storeUrl,
  messageSide,
}: {
  fulfillment: TrackableFulfillmentType[number];
  storeUrl: string | undefined;
  messageSide: "left" | "right";
}) {
  // Find the most recent completed event for the status badge
  const mostRecentCompletedEvent = fulfillment.trackingDetails
    ?.filter((event) => event.isCompleted)
    .slice(-1)[0];

  return (
    <Flex
      className={classNames(
        orderTrackingInfoMessageCss.orderTrackingInfoContainer,
        {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [orderTrackingInfoMessageCss.messageSideLeft]: messageSide === "left",
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [orderTrackingInfoMessageCss.messageSideRight]:
            messageSide === "right",
        },
      )}
      dir="column"
      gap="xl"
      justify="flex-start"
      p="xl"
    >
      <Flex dir="column" gap="2xl">
        {mostRecentCompletedEvent && (
          <Flex dir="row" gap="sm">
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              Status:
            </Text>
            <RedoBadge
              color={mostRecentCompletedEvent.color as RedoBadgeColor} //necessary to cast here to avoid moving the RedoBadgeColor enum to model (i think it makes more sense in web, except for this one use case)
              size="sm"
              text={mostRecentCompletedEvent.label}
              type="primary"
            />
          </Flex>
        )}
        <Flex>
          <StaticTrackingDetailsComponent
            details={fulfillment.trackingDetails}
          />
        </Flex>
        <Divider />
        <Flex justify="space-between">
          <Flex dir="column" gap="xs" overflow="hidden">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Tracking
            </Text>
            {fulfillment.redoTrackingPageLink ? (
              <ExternalLink
                iconClassName={orderTrackingInfoMessageCss.externalLinkIcon}
                showIcon
                url={fulfillment.redoTrackingPageLink}
              >
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  overflow="hidden"
                  textColor="primary"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {fulfillment.trackingNumber ?? "View"}
                </Text>
              </ExternalLink>
            ) : (
              "-"
            )}
          </Flex>
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Carrier
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {fulfillment.carrier ?? "-"}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex dir="column" gap="sm">
        <Text fontSize="sm" fontWeight="medium" textColor="secondary">
          Order summary
        </Text>
        <Flex dir="column" gap="xl">
          {fulfillment.lineItems.map((product, index) => (
            <ProductInfoCardButton
              key={index}
              onClickUrlOverride={fulfillment.redoTrackingPageLink}
              openInNewTab
              product={{
                ...product,
                variantId: product.variantId ?? undefined,
                variantTitle: product.variantTitle ?? undefined,
                url: product.url ?? undefined,
                handle: product.handle ?? undefined,
                imageUrl: product.imageUrl ?? undefined,
              }}
              storeUrl={storeUrl}
            />
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
});

const StaticTrackingDetailsComponent = memo(
  function StaticTrackingDetailsComponent({
    details,
  }: {
    details?: Array<{
      status: string;
      datetime?: string;
      label: string;
      isCompleted: boolean;
    }>;
  }) {
    const formatDate = (datetime: string) => {
      try {
        if (!datetime) return undefined;
        const date = Temporal.Instant.from(datetime);
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return date.toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: clientTimeZone,
        });
      } catch (e) {
        console.error("Error parsing date", e);
        return "Unknown";
      }
    };

    const FulfillmentDetailEntry = ({
      title,
      date,
      isCompleted,
    }: {
      title: string;
      date?: string;
      isCompleted: boolean;
    }) => (
      <Flex
        className={orderTrackingInfoMessageCss.historyEntry}
        dir="column"
        gap="xxs"
        justify="center"
        pl="3xl"
        position="relative"
      >
        {isCompleted && (
          <span className={orderTrackingInfoMessageCss.filledCircle} />
        )}
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textColor={isCompleted ? "primary" : "tertiary"}
        >
          {title}
        </Text>
        {date && (
          <Text fontSize="xs" fontWeight="regular" textColor="quaternary">
            {date}
          </Text>
        )}
      </Flex>
    );

    if (!details || details.length === 0) {
      return null;
    }

    return (
      <Flex dir="column" gap="sm">
        {details.map((event, index) => (
          <FulfillmentDetailEntry
            date={event.datetime ? formatDate(event.datetime) : undefined}
            isCompleted={event.isCompleted}
            key={index}
            title={event.label}
          />
        ))}
      </Flex>
    );
  },
);
